import { salesInvoiceForGrid } from '../../../spd/components/SalesInvoice/SalesInvoicesSearch';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CalculatePositionsResultDto,
  CreateUpdateSalesInvoiceDto,
  CreateUpdateSalesInvoiceDtoFromJSON,
  CreateUpdateSalesInvoicePositionDto,
  CreateUpdateSalesInvoicePositionDtoVatEnum,
  DefaultApi,
  GenerateAutoSalesInvoiceResultDto,
  GetLastEditorDto,
  GetSalesInvoiceDto,
  GetSalesInvoicePositionDto,
} from '../autogenerated/spdApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import {
  SalesInvoiceSchema,
  SalesInvoicePositionSchema,
  SalesInvoiceCorrectionPositionSchema,
} from '../validation/schemas';
import { PaymentFormEnum, SalesInvoiceStatusEnum } from '../validation/enums';

export interface SalesInvoicesContextInterface {
  getById: (id: string) => Promise<GetSalesInvoiceDto>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    status?: string,
    ksefStatuses?: string,
    onlyWithUnlinkedAmount?: boolean,
    statuses?: string,
    onlyLinkable?: boolean,
    paymentId?: string,
    onlyOverpaid?: boolean,
    examId?: string,
    companyId?: string,
    onlyOverdue?: boolean,
    onlyAutoGenerated?: boolean,
  ) => Promise<GridRecord[]>;

  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    status?: string,
    ksefStatuses?: string,
    onlyWithUnlinkedAmount?: boolean,
    statuses?: string,
    onlyLinkable?: boolean,
    paymentId?: string,
    onlyOverpaid?: boolean,
    examId?: string,
    companyId?: string,
    onlyOverdue?: boolean,
    onlyAutoGenerated?: boolean,
  ) => Promise<number>;
  create: (dto: CreateUpdateSalesInvoiceDto) => Promise<GetSalesInvoiceDto>;
  update: (id: string, dto: CreateUpdateSalesInvoiceDto) => Promise<void>;
  deactivate: (id: string) => Promise<void>;
  createCorrection: (id: string) => Promise<GetSalesInvoiceDto>;
  createFromPayment: (paymentId: string) => Promise<GetSalesInvoiceDto>;
  createFromExam: (examId: string) => Promise<GetSalesInvoiceDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getByExamId: (examId: string) => Promise<GetSalesInvoiceDto[]>;
  createPositions: (
    positions: CreateUpdateSalesInvoicePositionDto[],
    salesInvoiceId: string,
    salesInvoiceTs?: Date | null,
  ) => Promise<GetSalesInvoicePositionDto[]>;
  revertToDraft: (id: string) => Promise<void>;
  retryKsef: (id: string) => Promise<void>;
  addExams: (id: string, examIds: string[]) => Promise<void>;
  removeExam: (invoiceId: string, examId: string) => Promise<void>;
  generateAutoInvoice: (
    companyId: string,
    dateFrom: string,
    dateTo: string,
  ) => Promise<GenerateAutoSalesInvoiceResultDto>;
  calculatePositions: (
    invoiceId: string,
  ) => Promise<CalculatePositionsResultDto>;
}

export const SalesInvoicesContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.salesInvoiceControllerGet(id),

    async getAllByPortion(
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      status?: string,
      ksefStatuses?: string,
      onlyWithUnlinkedAmount?: boolean,
      statuses?: string,
      onlyLinkable?: boolean,
      paymentId?: string,
      onlyOverpaid?: boolean,
      examId?: string,
      companyId?: string,
      onlyOverdue?: boolean,
      onlyAutoGenerated?: boolean,
    ) {
      const data = await api.salesInvoiceControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText ?? '',
        filterColumns,
        status,
        statuses,
        ksefStatuses,
        onlyWithUnlinkedAmount?.toString(),
        onlyLinkable?.toString(),
        paymentId,
        onlyOverpaid?.toString(),
        examId,
        companyId,
        onlyOverdue?.toString(),
        onlyAutoGenerated?.toString(),
      );
      return data.map(salesInvoiceForGrid);
    },

    async getAllCount(
      filterText?: string,
      filterColumns?: string,
      status?: string,
      ksefStatuses?: string,
      onlyWithUnlinkedAmount?: boolean,
      statuses?: string,
      onlyLinkable?: boolean,
      paymentId?: string,
      onlyOverpaid?: boolean,
      examId?: string,
      companyId?: string,
      onlyOverdue?: boolean,
      onlyAutoGenerated?: boolean,
    ) {
      return api.salesInvoiceControllerGetAllCount(
        filterText ?? '',
        filterColumns,
        status,
        statuses,
        ksefStatuses,
        onlyWithUnlinkedAmount?.toString(),
        onlyLinkable?.toString(),
        paymentId,
        onlyOverpaid?.toString(),
        examId,
        companyId,
        onlyOverdue?.toString(),
        onlyAutoGenerated?.toString(),
      );
    },

    create: (dto: CreateUpdateSalesInvoiceDto) =>
      api.salesInvoiceControllerCreate(dto),

    update: (id: string, dto: CreateUpdateSalesInvoiceDto) =>
      api.salesInvoiceControllerUpdate(id, dto),

    deactivate: (id: string) => api.salesInvoiceControllerDeactivate(id),

    createCorrection: (id: string) =>
      api.salesInvoiceControllerCreateCorrection(id),

    createFromPayment: (paymentId: string) =>
      api.salesInvoiceControllerCreateFromPayment(paymentId),

    createFromExam: (examId: string) =>
      api.salesInvoiceControllerCreateFromExam(examId),

    getLastEditor: (id: string) => api.salesInvoiceControllerGetLastEditor(id),

    getByExamId: (examId: string) =>
      api.salesInvoiceControllerGetByExam(examId),

    revertToDraft: (id: string) => api.salesInvoiceControllerRevertToDraft(id),

    retryKsef: (id: string) => api.salesInvoiceControllerRetryKsef(id),

    addExams: (id: string, examIds: string[]) =>
      api.salesInvoiceControllerAddExams(id, { examIds }),

    removeExam: (invoiceId: string, examId: string) =>
      api.salesInvoiceControllerRemoveExam(invoiceId, examId),

    generateAutoInvoice: (
      companyId: string,
      dateFrom: string,
      dateTo: string,
    ) =>
      api.salesInvoiceControllerGenerateAutoInvoice({
        companyId,
        dateFrom,
        dateTo,
      }),

    calculatePositions: (invoiceId: string) =>
      api.salesInvoiceControllerCalculatePositions(invoiceId),

    createPositions: (
      positions: CreateUpdateSalesInvoicePositionDto[],
      salesInvoiceId: string,
      salesInvoiceTs?: Date | null,
    ) =>
      api.salesInvoicePositionControllerCreateMany({
        positions,
        salesInvoiceId,
        salesInvoiceTs,
      }),
  };
};

export const newSalesInvoice = (): CreateUpdateSalesInvoiceDto => {
  const invoiceDate = new Date();
  const dueDate = new Date(invoiceDate);
  dueDate.setDate(dueDate.getDate() + 14);
  return CreateUpdateSalesInvoiceDtoFromJSON({
    number: '',
    status: SalesInvoiceStatusEnum.Draft,
    paymentForm: PaymentFormEnum.Cash,
    invoiceDate,
    saleDate: new Date(),
    dueDate,
    isPaid: false,
    payableDaysId: null,
    active: true,
    toKsef: false,
    sendEmail: true,
  });
};

export const convertSalesInvoice = (
  salesInvoice: GetSalesInvoiceDto,
): CreateUpdateSalesInvoiceDto => {
  const ex = salesInvoice.examined;

  return CreateUpdateSalesInvoiceDtoFromJSON({
    ...salesInvoice,
    ...{
      companyId: salesInvoice.company?.id || null,
      examinedId: ex?.id || null,
      companyNip: salesInvoice.company?.nip ?? null,
      companyTaxIdentifier: salesInvoice.company?.taxIdentifier ?? null,
      examinedNip: ex?.nip ?? null,
      companyAddress: salesInvoice.company?.address ?? null,
      examinedAddress: (ex?.invoiceAddress || ex?.city) ?? null,
      originalInvoiceId: salesInvoice.originalInvoice?.id ?? null,
      sendEmail: salesInvoice.sendEmail ?? true,
    },
  });
};

export const newSalesInvoicePosition = (
  order: number,
  salesInvoiceId: string,
): CreateUpdateSalesInvoicePositionDto => ({
  order,
  name: '',
  amount: '0',
  quantity: 1,

  vat: CreateUpdateSalesInvoicePositionDtoVatEnum.zw,
  active: true,
  salesInvoiceId: salesInvoiceId,
});

export const validate = (salesInvoice: CreateUpdateSalesInvoiceDto) => {
  return validateAgainst(SalesInvoiceSchema, salesInvoice);
};

export const validatePosition = (
  position: CreateUpdateSalesInvoicePositionDto,
  isCorrection?: boolean,
) => {
  return validateAgainst(
    isCorrection
      ? SalesInvoiceCorrectionPositionSchema
      : SalesInvoicePositionSchema,
    position,
  );
};
